import { TReactSetState, TUseTranslationTfn } from "../../data/AppType";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
} from "../../utils/AppFunctions";
import * as Yup from "yup";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  CREATE_MEASUREMENT_FIELD,
  GET_MEASUREMENT_FIELD_BY_ID,
  GLOBAL_DROP_DOWN,
  UPDATE_MEASUREMENT_FIELD,
} from "../../services/Endpoints";
import { IOption } from "../../data/AppInterface";
import { LOOKUP_CATEGORY } from "../../utils/AppEnumerations";

export interface IMeasurementFieldForm {
  id?: string;
  measurement_field_name: string;
  measurement_unit: string;
  measurement_field_image_url: string;
}

export const MEASUREMENT_FIELD_INITIAL_VALUES = {
  measurement_field_name: "",
  measurement_unit: "",
  measurement_field_image_url: "",
};

export const measurementFieldValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    measurement_field_name: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("measurement_field_name")],
        ])
      )
      .max(
        50,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("measurement_field_name")],
          ["max", "50"],
        ])
      )
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("measurement_field_name")],
          ["min", "2"],
        ])
      ),

    measurement_unit: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("measurement_unit")],
        ])
      )
      .max(
        50,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("measurement_unit")],
          ["max", "50"],
        ])
      )
      .min(
        1,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("measurement_unit")],
          ["min", "1"],
        ])
      ),
  });

export const fetchMeasurementFieldById = async (
  id: string,
  handleClose: (status: boolean) => void
) => {
  try {
    const measurementFieldData = await GET_MEASUREMENT_FIELD_BY_ID(id);
    if (
      measurementFieldData &&
      measurementFieldData.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      return measurementFieldData.data;
    } else {
      handleClose(false);
      sweetAlertError({
        message: measurementFieldData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleClose(false);
    sweetAlertError({ message: e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
    handleError(e, "fetchMeasurementFieldById");
  }
};

export const handleSubmitMeasurementField = async (
  values: IMeasurementFieldForm,
  setLoading: TReactSetState<boolean>,
  id: string | undefined,
  handleClose: (status: boolean) => void
) => {
  setLoading(true);
  try {
    let resultMeasurementField;
    if (id) {
      resultMeasurementField = await UPDATE_MEASUREMENT_FIELD(values, id);
    } else {
      resultMeasurementField = await CREATE_MEASUREMENT_FIELD(values);
    }
    if (
      resultMeasurementField &&
      resultMeasurementField.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      sweetAlertSuccess({
        message: resultMeasurementField.message,
      });
      return handleClose(true);
    } else {
      sweetAlertError({
        message:
          resultMeasurementField?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    sweetAlertError({ message: e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED });
    handleError(e, "handleSubmitMeasurementField");
  } finally {
    setLoading(false);
  }
};

export const getMeasurementTypeUnitDdl = async (
  setMeasurementTypeUnit: TReactSetState<IOption<string>[]>,
  selectedGanderValue?: string
) => {
  try {
    const payload = {
      entity: "sts_lookups",
      data_value: "data_value",
      display_value: "display_value",
      filters: [
        {
          field: "lookup_category",
          value: LOOKUP_CATEGORY.MeasurementUnit,
          condition: "eq",
        },
      ],
      ...(selectedGanderValue && {
        lookup_values: [selectedGanderValue],
        lookup_category: LOOKUP_CATEGORY.MeasurementUnit,
      }),
    };
    const MeasurementTypeUnitDDLList = await GLOBAL_DROP_DOWN(payload);
    if (
      MeasurementTypeUnitDDLList &&
      MeasurementTypeUnitDDLList.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      if (MeasurementTypeUnitDDLList.data) {
        setMeasurementTypeUnit(MeasurementTypeUnitDDLList.data);
      } else {
        setMeasurementTypeUnit([]);
      }
    } else {
      toastError(
        MeasurementTypeUnitDDLList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED
      );
    }
  } catch (e: any) {
    toastError(e?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    handleError(e, "getMeasurementTypeUnitDdl");
  }
};
